import type { ContractFeeCode } from '../contract/contract-fee-codes';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { TerritoryCurrency } from '../territory/territory.interface';

/**
 * Unit the territory's annual mileage options are expressed in (SWOOP-339). Changing it relabels the
 * stored figures; it never converts them.
 */
export const DISTANCE_UNIT = { km: 'km', mi: 'mi' } as const;

export type DistanceUnit = (typeof DISTANCE_UNIT)[keyof typeof DISTANCE_UNIT];

/**
 * What an extra fee's amount is applied to (SWOOP-343): a per mille or a percent of the contract value, or a
 * fixed amount per contract in the territory's currency. Calculating the fee on a contract is SWOOP-346's.
 */
export const EXTRA_FEE_BASIS = {
  PER_MILLE_OF_CONTRACT_VALUE: 'PER_MILLE_OF_CONTRACT_VALUE',
  PERCENT_OF_CONTRACT_VALUE: 'PERCENT_OF_CONTRACT_VALUE',
  FIXED_PER_CONTRACT: 'FIXED_PER_CONTRACT',
} as const;

export type ExtraFeeBasis = (typeof EXTRA_FEE_BASIS)[keyof typeof EXTRA_FEE_BASIS];

/**
 * One extra fee or tax a territory's new contracts carry (SWOOP-343), e.g. stamp duty. Names may repeat;
 * the list keeps the order the admin set. A mandatory fee always applies on the contract; an optional one
 * can be ticked there (SWOOP-346). `code` says what the fee is (SWOOP-416); a territory holds at most one fee per
 * code other than `OTHER`.
 */
export interface ExtraFee {
  code: ContractFeeCode;
  name: string;
  amount: number;
  basis: ExtraFeeBasis;
  mandatory: boolean;
}

/**
 * The lease options a territory offers (SWOOP-339), surfaced on Territory › Leasing.
 *
 * - `terms` are lease durations in months; `annualMileages` are allowed yearly distances in
 *   `distanceUnit`. Both are stored sorted ascending.
 * - `quoteValidityDays` is how long a quote stays valid after it is issued.
 *
 * The pricing parameters (SWOOP-340) are the territory-wide rates the pricing engine reads, each with at
 * most 2 decimals. Vehicle-dependent costs (maintenance, tyres, excess mileage, residual value) are NOT
 * here: they are read from each vehicle in the fleet core.
 */
export interface LeasingSettingsValues {
  terms: number[];
  annualMileages: number[];
  quoteValidityDays: number;
  distanceUnit: DistanceUnit;
  /** Cost of funds, percent per year. */
  costOfFundsPercent: number;
  /** Margin, percent. */
  marginPercent: number;
  /** Administration fee per month, in the territory's currency (never converted). */
  adminFeeMonthly: number;
  /** Lease-to-own option, percent of capital. */
  leaseToOwnPercent: number;
  /**
   * Default payment terms (SWOOP-354): the invoicing cycles and payment due options a contract may pick, as
   * free-text labels in the order the admin added them, never translated. A new contract starts on
   * "Monthly in advance" and "Net 30", or the first option offered if those are removed (applied by SWOOP-346).
   */
  invoicingCycles: string[];
  paymentDueOptions: string[];
  /** Late-payment interest, percent per year. How Billing applies it is the Billing epic's. */
  lateInterestPercent: number;
  /** Rent increase on a contract that runs past its end date, percent (holdover, SWOOP-385). */
  rentIncreaseAfterEndPercent: number;
  /** Extra fees and taxes added to every new contract (SWOOP-343). */
  extraFees: ExtraFee[];
}

/** One document per (tenant, territory), stored in `leasing_settings`. Absent until the first save. */
export interface LeasingSettings extends BaseDocument, LeasingSettingsValues {
  territoryId: string;
}

export type CreateLeasingSettings = Omit<LeasingSettings, keyof BaseDocument>;

/**
 * GET response: the stored document, or {@link LEASING_SETTINGS_DEFAULTS} when the territory has never
 * saved. `updatedAt` is echoed back as `baseUpdatedAt` on save (the stale-save token); `null` means
 * "never saved".
 */
export interface LeasingSettingsView extends LeasingSettingsValues {
  id: string | null;
  territoryId: string;
  updatedAt: string | null;
  /** The territory's current currency: the unit of `adminFeeMonthly` and the key of its cap. Not stored. */
  currency: TerritoryCurrency;
}

/**
 * Values served for a territory that has never saved, and the baseline written on its first save.
 * Callers that spread this into a mutable object must copy the arrays too, so they never share them.
 */
export const LEASING_SETTINGS_DEFAULTS = {
  terms: [24, 36, 48, 60, 72],
  annualMileages: [15_000, 20_000, 30_000, 40_000, 50_000],
  quoteValidityDays: 7,
  distanceUnit: DISTANCE_UNIT.km,
  costOfFundsPercent: 9,
  marginPercent: 25,
  adminFeeMonthly: 22,
  leaseToOwnPercent: 1,
  invoicingCycles: ['Monthly in advance', 'Quarterly in advance'],
  paymentDueOptions: ['Cash', 'Net 30', 'Net 45', 'Net 60'],
  lateInterestPercent: 9,
  rentIncreaseAfterEndPercent: 10,
  extraFees: [
    {
      code: 'STAMP_DUTY',
      name: 'Stamp duty',
      amount: 4,
      basis: EXTRA_FEE_BASIS.PER_MILLE_OF_CONTRACT_VALUE,
      mandatory: true,
    },
  ],
} satisfies LeasingSettingsValues;

/** Validation bounds shared by the API DTO and the settings form (SWOOP-339, SWOOP-340, SWOOP-354, SWOOP-343). */
export const LEASING_SETTINGS_LIMITS = {
  maxOptions: 20,
  termMonthsMax: 240,
  annualMileageMax: 1_000_000,
  quoteValidityDays: { min: 1, max: 180 },
  /** Upper bound of the three percentage rates; every pricing parameter's minimum is 0. */
  percentMax: 100,
  /** Decimal places a pricing parameter, a payment-terms rate or a fee amount may carry. */
  pricingDecimals: 2,
  /** Longest invoicing cycle or payment due label, after trimming. */
  optionLabelMax: 40,
  /** Longest extra fee name, after trimming. */
  feeNameMax: 60,
  /** Most extra fees a territory may hold. */
  maxFees: 20,
  /** Upper bound of a per-mille fee amount. */
  perMilleMax: 1000,
} as const;

/**
 * Upper bound of `adminFeeMonthly`, per territory currency (SWOOP-340): a technical guard well above any
 * real fee. LBP figures are roughly 89,500 times USD ones, hence its own cap. Adding a currency fails to
 * compile until it is given a cap here.
 */
export const LEASING_ADMIN_FEE_MAX_BY_CURRENCY = {
  EUR: 10_000,
  GBP: 10_000,
  LBP: 1_000_000_000,
  USD: 10_000,
} as const satisfies Record<TerritoryCurrency, number>;

/** The largest amount any fee basis allows in any currency: the DTO's outer bound before the real check. */
export const EXTRA_FEE_AMOUNT_MAX_ANY = Math.max(
  LEASING_SETTINGS_LIMITS.perMilleMax,
  LEASING_SETTINGS_LIMITS.percentMax,
  ...Object.values(LEASING_ADMIN_FEE_MAX_BY_CURRENCY),
);

const EXTRA_FEE_MAX_BY_BASIS: Record<ExtraFeeBasis, (currency: TerritoryCurrency) => number> = {
  [EXTRA_FEE_BASIS.PER_MILLE_OF_CONTRACT_VALUE]: () => LEASING_SETTINGS_LIMITS.perMilleMax,
  [EXTRA_FEE_BASIS.PERCENT_OF_CONTRACT_VALUE]: () => LEASING_SETTINGS_LIMITS.percentMax,
  // Same technical guard as the admin fee (SWOOP-340): a fixed fee is money in the territory's currency.
  [EXTRA_FEE_BASIS.FIXED_PER_CONTRACT]: (currency) => LEASING_ADMIN_FEE_MAX_BY_CURRENCY[currency],
};

/**
 * Upper bound of an extra fee's amount (SWOOP-343): 1,000 for a per mille, 100 for a percent, the
 * currency's cap for a fixed amount. One rule for the API (service) and the settings form.
 */
export function extraFeeAmountMax(basis: ExtraFeeBasis, currency: TerritoryCurrency): number {
  return EXTRA_FEE_MAX_BY_BASIS[basis](currency);
}
